import { useEffect, useRef, useState, type ChangeEvent } from 'react'
import { Textarea } from '@/components/ui/textarea'
import { Label } from '@/components/ui/label'

type Status = 'idle' | 'saving' | 'saved' | 'error'

type Props = {
  guestId: string
  initialValue: string
  debounceMs?: number
}

function csrfToken() {
  return document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content ?? ''
}

function relativeTimeFrom(date: Date) {
  const seconds = Math.floor((Date.now() - date.getTime()) / 1000)
  if (seconds < 5) return 'just now'
  if (seconds < 60) return `${seconds}s ago`
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`
  return date.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })
}

export default function NotesAutosave({ guestId, initialValue, debounceMs = 800 }: Props) {
  const [value, setValue] = useState(initialValue ?? '')
  const [status, setStatus] = useState<Status>('idle')
  const [savedAt, setSavedAt] = useState<Date | null>(null)
  const [, setTick] = useState(0)
  const timer = useRef<number | null>(null)
  const abortRef = useRef<AbortController | null>(null)
  const lastSavedValue = useRef(initialValue ?? '')

  // Force the relative-time pill to re-render every 30s.
  useEffect(() => {
    const id = window.setInterval(() => setTick(t => t + 1), 30_000)
    return () => window.clearInterval(id)
  }, [])

  useEffect(() => () => {
    if (timer.current) window.clearTimeout(timer.current)
    abortRef.current?.abort()
  }, [])

  function onChange(e: ChangeEvent<HTMLTextAreaElement>) {
    const next = e.target.value
    setValue(next)
    if (timer.current) window.clearTimeout(timer.current)
    timer.current = window.setTimeout(() => save(next), debounceMs)
  }

  async function save(next: string) {
    if (next === lastSavedValue.current) return

    abortRef.current?.abort()
    const controller = new AbortController()
    abortRef.current = controller

    setStatus('saving')

    try {
      const res = await fetch(`/dashboard/guests/${guestId}`, {
        method: 'PATCH',
        signal: controller.signal,
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          'X-CSRF-Token': csrfToken(),
          'X-Requested-With': 'XMLHttpRequest',
        },
        body: JSON.stringify({ guest: { notes: next }, autosave: '1' }),
      })

      if (res.redirected || res.status !== 204) throw new Error(`autosave_failed_${res.status}`)
      lastSavedValue.current = next
      setSavedAt(new Date())
      setStatus('saved')
    } catch (err) {
      if ((err as Error).name === 'AbortError') return
      setStatus('error')
    }
  }

  let pillCopy = ''
  let pillClass = 'text-muted-foreground'
  if (status === 'saving') {
    pillCopy = 'Saving…'
  } else if (status === 'saved' && savedAt) {
    pillCopy = `Saved · ${relativeTimeFrom(savedAt)}`
  } else if (status === 'error') {
    pillCopy = 'Save failed — retrying on next change'
    pillClass = 'text-destructive'
  }

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <Label htmlFor={`notes-${guestId}`}>Notes</Label>
        <span aria-live="polite" className={`text-xs ${pillClass}`}>
          {pillCopy}
        </span>
      </div>
      <Textarea
        id={`notes-${guestId}`}
        value={value}
        onChange={onChange}
        placeholder="Personal notes about this guest — only visible to your team."
        rows={5}
      />
    </div>
  )
}
